import { Dialog, DialogContent, Grid, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
    generateBulkDynamicObjectPayload,
    getFormattedOptionsValue,
    getLineItemFieldsByName,
    getObjectIdsByTempId,
    getRequiredLineItemFieldNames,
    getRowsFromSif,
    SIF_IDENTIFIERS,
    generateRowsForBulkCreate
} from './utils';
import { CustomDialogActions, CustomDialogTitle, PreviewGridSection, FileSection, RenderActions, ImportResultCodes } from './components';
import { GridApiPro, GridColDef, useGridApiRef } from '@mui/x-data-grid-pro';
import { useLineItemsData, useLineItemsMutations } from '../LineItems/hooks';
import { LineItemRowItemV2Input, LineItemRowV2Input } from '../LineItems/types';
import { openSnackbar } from 'store/slices/snackbar';
import { useDispatch } from 'store';
import { ISifRowsCodesStatus, SifOption } from './types';
import { Add } from '@mui/icons-material';
import { ObjectPanel } from './components/ObjectPanel';
import { useQuery, useMutation } from '@apollo/client';
import {
    BulkCreateDynamicObject,
    BulkCreateDynamicObjectVariables,
    FindObjects,
    FindObjectsVariables
} from 'views/backoffice/CustomObjects/types';
import { QUERY_FIND_OBJECT } from 'graphql/queries/customObjects';
import { BULK_CREATE_DYNAMIC_OBJECT } from 'graphql/mutations/dynamicObject';
import { OPTION_FIELD_NAME } from '../LineItems/constants';

export type ImportLineItemsProps = {
    open: boolean;
    onClose: () => void;
    onSubmit: () => void;
    sifFile: File | null;
    apiRef?: React.MutableRefObject<GridApiPro>;
    recordTypeId: number;
    recordId: number;
    baseIndexForTheCreation?: number;
} & ({ manualCreation?: false } | { manualCreation: true; onSubmitCreation?: (rows: Record<string, any>[]) => void });

export const ImportLineItems = ({
    open,
    onClose,
    onSubmit,
    sifFile,
    apiRef,
    recordTypeId,
    recordId,
    baseIndexForTheCreation,
    ...props
}: ImportLineItemsProps) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const { palette } = useTheme();
    const previewGridApiRef = useGridApiRef();

    const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
    const [selectedSifFile, setSelectedSifFile] = useState<File | null>(sifFile || null);
    const [selectedMergeMode, setSelectedMergeMode] = useState('IMPORT_ALL');
    const [rows, setRows] = useState<Record<string, any>[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sidePanelMode, setSidePanelMode] = useState<'none' | 'options' | 'result'>('none');
    const [sifRowsCodesStatus, setSifRowsCodesStatus] = useState<ISifRowsCodesStatus>();
    const [selectedOptionRowId, setSelectedOptionRowId] = useState<number>();

    const handleToggleEdit = (rowId: string | number) => () => {
        if (previewGridApiRef.current.state.editRows[rowId]) previewGridApiRef.current.stopRowEditMode({ id: rowId });
        else previewGridApiRef.current.startRowEditMode({ id: rowId });
    };

    const handleClickDelete = (rowId: string | number) => () => {
        setRows((prevRows) => prevRows.filter((row) => row.id !== rowId));
    };

    const handleOpenOptions = (rowId: number) => {
        const isNewOption = selectedOptionRowId !== rowId;
        setSelectedOptionRowId(isNewOption ? rowId : undefined);

        if (isNewOption) setSidePanelMode('options');
        else setSidePanelMode('none');
    };

    const { lineItemHeaders } = useLineItemsData(recordTypeId as number);
    const { createLineItemRows } = useLineItemsMutations(recordId);
    const { data: objectData, loading: loadingFindObjects } = useQuery<FindObjects, FindObjectsVariables>(QUERY_FIND_OBJECT, {
        variables: { data: { names: ['Options'] } }
    });

    const objectDefinition = objectData?.findObjects?.[0];
    const showOptionsSidePanel = sidePanelMode === 'options' && !!selectedOptionRowId && !loadingFindObjects && objectDefinition;
    const showSifImportResultCodes = sidePanelMode === 'result' && sifRowsCodesStatus;

    const [bulkCreateDynamicObjects] = useMutation<BulkCreateDynamicObject, BulkCreateDynamicObjectVariables>(BULK_CREATE_DYNAMIC_OBJECT);

    const columns = apiRef
        ? [
              ...apiRef.current.getAllColumns().filter((el) => el.field !== 'actions' && el.field.split('-')?.at(0) !== 'Options'),
              {
                  field: 'actions',
                  headerName: intl.formatMessage({ id: 'actions', defaultMessage: 'Actions' }),
                  renderHeader: (params) => (
                      <Typography fontSize="16px" fontWeight={700} color="#000000">
                          {params.colDef.headerName}
                      </Typography>
                  ),
                  renderCell: (params) => (
                      <RenderActions
                          key={previewGridApiRef.current.state.editRows[params.id] ? `${params.id}-editing` : `${params.id}-view`}
                          onToggleEdit={handleToggleEdit(params.id)}
                          onDelete={handleClickDelete(params.id)}
                          rowId={params.id}
                          apiRef={previewGridApiRef}
                      />
                  )
              } as GridColDef,
              {
                  field: 'options',
                  headerName: 'Options',
                  renderHeader: (params) => (
                      <Typography fontSize="16px" fontWeight={700} color="#000000">
                          {params.colDef.headerName}
                      </Typography>
                  ),
                  renderCell: ({ value: options, ...params }) => (
                      <Typography
                          onClick={() => handleOpenOptions(+params.id)}
                          sx={(theme) => ({
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              color: theme.palette.primary[400],
                              cursor: 'pointer',
                              textDecoration: 'underline'
                          })}
                      >
                          {getFormattedOptionsValue(options)?.length ? (
                              `${getFormattedOptionsValue(options)}`
                          ) : (
                              <>
                                  <Add fontSize="small" /> Add Data
                              </>
                          )}
                      </Typography>
                  )
              } as GridColDef
          ]
        : [];

    const handleSubmit = async () => {
        setIsSubmitting(true);

        const selectedRowsToCreate = selectedRows.length ? rows.filter((row) => selectedRows.includes(row.id)) : rows;

        if (props.manualCreation) {
            props.onSubmitCreation?.(selectedRowsToCreate);
            dispatch(
                openSnackbar({
                    open: true,
                    type: 'success',
                    title: 'Import completed',
                    subtitle: `${selectedRowsToCreate.length} items created successfully`
                })
            );
            setIsSubmitting(false);
            onClose();
            return;
        }

        try {
            // Creamos una función para transformar los datos a lo que espera createLineItemRows

            const rowsForBulkCreate = generateRowsForBulkCreate(selectedRowsToCreate, lineItemHeaders);

            await createLineItemRows({
                variables: {
                    data: {
                        rows: rowsForBulkCreate as LineItemRowV2Input[],
                        recordHeaderId: recordId
                    }
                }
            });

            setIsSubmitting(false);
            onSubmit();

            dispatch(
                openSnackbar({
                    open: true,
                    type: 'success',
                    title: 'Import completed',
                    subtitle: `${selectedRowsToCreate.length} items created successfully`
                })
            );
        } catch (error) {
            console.error('Error during import:', error);
            setIsSubmitting(false);
        }
    };

    // const processOptionsArray = async (options: SifOption[]): Promise<string> => {
    //     try {
    //         // Si el array options está vacío o no tiene elementos, retornar string vacío
    //         if (!options || options.length === 0) {
    //             return '';
    //         }

    //         // Si no tenemos la definición del objeto, no podemos crear dynamic objects
    //         if (!objectDefinition) {
    //             console.warn('Object definition not found for Options');
    //             return '';
    //         }

    //         const createdObjectIds: number[] = [];

    //         // Crear dynamic objects para cada opción
    //         for (const option of options) {
    //             // Filtrar propiedades que tienen valor (no vacías, null, undefined)
    //             const validAttributes = Object.entries(option).filter(
    //                 ([key, value]) => value !== null && value !== undefined && value !== '' && key !== 'id'
    //             );

    //             // Solo crear el objeto si tiene al menos una propiedad con valor
    //             if (validAttributes.length === 0) {
    //                 // eslint-disable-next-line no-continue
    //                 continue;
    //             }

    //             // Convertir atributos válidos al formato de propiedades del objeto
    //             const objectValues: Record<string, any> = {};

    //             validAttributes.forEach(([propName, value]) => {
    //                 const property = objectDefinition.objectProperties?.find((p) => p.name === propName);
    //                 if (property) {
    //                     objectValues[property.id] = value;
    //                 } else {
    //                     console.warn(`Property not found for ${propName} in Options object definition`);
    //                 }
    //             });

    //             // Crear el dynamic object
    //             const dynamicObjectData = {
    //                 data: {
    //                     objectDefinitionId: Number(objectDefinition.id),
    //                     values: Object.entries(objectValues).map(([propertyId, value]) => ({
    //                         propertyId: Number(propertyId),
    //                         value: String(value)
    //                     }))
    //                 }
    //             };

    //             try {
    //                 // eslint-disable-next-line no-await-in-loop
    //                 const { data } = await registerDynamicObject({ variables: dynamicObjectData });
    //                 if (data?.registerOrUpdateDynamicObject.id) {
    //                     createdObjectIds.push(Number(data.registerOrUpdateDynamicObject.id));
    //                 }
    //             } catch (error) {
    //                 console.error('Error creating dynamic object for option:', error);
    //                 // Continuar con las siguientes opciones aunque una falle
    //             }
    //         }

    //         // Retornar array stringify de IDs de los dynamic objects creados
    //         return JSON.stringify(createdObjectIds);
    //     } catch (error) {
    //         console.error('Error processing options array:', error);
    //         return '';
    //     }
    // };

    const processAllOptionsArray = async (options: SifOption[]): Promise<{ error: string } | Record<number, number>> => {
        try {
            if (!options || options.length === 0) return { error: 'No options provided' };

            if (!objectDefinition) {
                console.warn('Object definition not found for Options');
                return { error: 'Object definition not found for Options' };
            }

            const createdObjectByTempId: Record<number, any> = {};
            const objectToCreate = generateBulkDynamicObjectPayload(options, objectDefinition);

            // console.log('options', options);
            // console.log('objectToCreate', objectToCreate);
            const { data } = await bulkCreateDynamicObjects({ variables: { data: objectToCreate } });

            // console.log('data', data?.bulkCreateDynamicObjects);
            if (data?.bulkCreateDynamicObjects) {
                data.bulkCreateDynamicObjects.forEach((obj) => {
                    createdObjectByTempId[+obj.tempId] = obj.id;
                });
            }

            return createdObjectByTempId;
        } catch (error) {
            console.error('Error processing options array:', error);
            return { error: 'Error processing options array' };
        }
    };

    // const handleCreateLineItem = async (rowToCreate: Record<string, any>, index: number) => {
    //     try {
    //         // Procesar el array options antes de crear el line item
    //         const processedOptionsValue = await processOptionsArray(rowToCreate.options || []);

    //         const items = Object.keys(rowToCreate)
    //             .filter((key) => !['id', 'autogenerate', 'order', 'options'].includes(key) && rowToCreate[key])
    //             .filter((key) => {
    //                 const [fieldNameFromUpdated, fieldOrder] = key.split('-');
    //                 const lineItemsByTypeFiledsId = Number(
    //                     lineItemHeaders?.lineItemsByTypeFileds.find(
    //                         (el) => el.recordAdditionalFields.name === fieldNameFromUpdated && el.order === Number(fieldOrder)
    //                     )?.id
    //                 );
    //                 return !!lineItemsByTypeFiledsId;
    //             })
    //             .map((key) => {
    //                 const [fieldNameFromUpdated, fieldOrder] = key.split('-');
    //                 const lineItemsByTypeFiledsId = Number(
    //                     lineItemHeaders?.lineItemsByTypeFileds.find(
    //                         (el) => el.recordAdditionalFields.name === fieldNameFromUpdated && el.order === Number(fieldOrder)
    //                     )?.id
    //                 );

    //                 return {
    //                     lineItemsByTypeFiledsId,
    //                     value: String(rowToCreate[key])
    //                 } as LineItemsFields;
    //             });

    //         // Agregar el campo options procesado si tiene valor
    //         if (processedOptionsValue) {
    //             // Buscar el campo options en los lineItemHeaders
    //             const optionsField = lineItemHeaders?.lineItemsByTypeFileds.find(
    //                 (el) => el.recordAdditionalFields.name.toLowerCase() === 'options'
    //             );

    //             if (optionsField) {
    //                 items.push({
    //                     lineItemsByTypeFiledsId: Number(optionsField.id),
    //                     value: processedOptionsValue
    //                 } as LineItemsFields);
    //             }
    //         }

    //         if (recordId !== 0) {
    //             await createOrUpdateByRow({
    //                 variables: { data: { index, lineItemsFields: items, recordHeaderId: recordId } }
    //             });
    //         }

    //         return { success: true };
    //     } catch (error) {
    //         console.error('Error during import:', error);
    //         return { success: false, error };
    //     }
    // };

    const formatRowsByLineItemHeaders = useCallback(
        (rowList: Record<string, any>[]) => {
            const newList = [];
            let sifCodesStatus: ISifRowsCodesStatus = { recognizedCodes: [], unrecognizedCodes: [], requiredButMissingCodes: [] };

            const lineItemFieldsByName = getLineItemFieldsByName(lineItemHeaders?.lineItemsByTypeFileds || []);
            const requiredLineItemsFieldNames: string[] = getRequiredLineItemFieldNames(lineItemHeaders?.lineItemsByTypeFileds || []);

            for (const row of rowList) {
                const newRow = Object.fromEntries(
                    Object.entries(row).map(([key, value]) => {
                        const lineItemFieldDef = lineItemFieldsByName[key];
                        if (lineItemFieldDef) return [`${key}-${lineItemFieldDef.order}`, value];
                        return [key, value];
                    })
                );

                const allKeysOfCurrentRow = Object.keys(row);
                const allSifIdentifiersValues = Object.values(SIF_IDENTIFIERS);

                const recognizedCodes = allKeysOfCurrentRow?.filter((item) => allSifIdentifiersValues.includes(item));
                const unrecognizedCodes = allKeysOfCurrentRow?.filter(
                    (item) => !allSifIdentifiersValues.includes(item) && !['options', 'id'].includes(item)
                );
                const requiredButMissingKeys = requiredLineItemsFieldNames.filter((item) => !allKeysOfCurrentRow.includes(item));

                sifCodesStatus = {
                    recognizedCodes: [...new Set([...sifCodesStatus.recognizedCodes, ...recognizedCodes])],
                    unrecognizedCodes: [...new Set([...sifCodesStatus.unrecognizedCodes, ...unrecognizedCodes])],
                    requiredButMissingCodes: [...new Set([...sifCodesStatus.requiredButMissingCodes, ...requiredButMissingKeys])]
                };

                newList.push(newRow);
            }

            setRows(newList);
            setSifRowsCodesStatus(sifCodesStatus);
            setSidePanelMode('result');
            setTimeout(() => {
                previewGridApiRef.current.autosizeColumns({ includeHeaders: true });
            }, 1000);
        },
        [lineItemHeaders?.lineItemsByTypeFileds, previewGridApiRef]
    );

    const handleChangeOption = (option: SifOption) => {
        setRows((_rows) =>
            _rows?.map((row) =>
                +row?.id === selectedOptionRowId
                    ? {
                          ...row,
                          options: row.options?.map((curOption: SifOption) =>
                              Number(curOption?.tempId) === Number(option?.tempId) ? { ...option } : curOption
                          )
                      }
                    : row
            )
        );
    };

    const handleDeleteOption = (optionId: number) => {
        setRows((_rows) =>
            _rows?.map((row) =>
                +row?.id === selectedOptionRowId
                    ? {
                          ...row,
                          options: row.options?.filter((curOption: SifOption) => Number(curOption?.tempId) !== optionId)
                      }
                    : row
            )
        );
    };

    const handleAddOption = () => {
        setRows((_rows) =>
            _rows.map((row) =>
                +row?.id === selectedOptionRowId
                    ? {
                          ...row,
                          options: [
                              ...row.options,
                              {
                                  tempId: row.options?.length ? +row.options.at(-1)?.tempId + 1 : 1,
                                  'Option Number': '',
                                  'Option Description': '',
                                  'Option Group': '',
                                  'Option List Up charge': ''
                              }
                          ]
                      }
                    : row
            )
        );
    };

    useEffect(() => {
        if (selectedSifFile && lineItemHeaders) {
            getRowsFromSif(selectedSifFile, formatRowsByLineItemHeaders);
        }
    }, [selectedSifFile, lineItemHeaders, formatRowsByLineItemHeaders]);

    return (
        <>
            <Dialog
                open={open}
                onClose={isSubmitting ? undefined : onClose}
                fullWidth
                maxWidth="xl"
                PaperProps={{ sx: { borderRadius: '8px', py: 0 } }}
            >
                <CustomDialogTitle
                    onClose={onClose}
                    title={intl.formatMessage({ id: 'importLineItems', defaultMessage: 'Import Line Items' })}
                />

                <DialogContent sx={{ overflowY: 'hidden', height: '80vh', p: 0, bgcolor: palette.primary[300] }}>
                    <Grid container sx={{ height: '100%', maxWidth: '100%' }} flexWrap="nowrap">
                        <FileSection
                            onChangeMergeMode={setSelectedMergeMode}
                            onChangeSelectedSifFile={(val) => {
                                setSelectedSifFile(val);
                                if (val === null) {
                                    setRows([]);
                                    setSelectedRows([]);
                                }
                            }}
                            selectedMergeMode={selectedMergeMode}
                            selectedSifFile={selectedSifFile}
                        />
                        <PreviewGridSection
                            rows={rows}
                            columns={columns}
                            selectedRows={selectedRows}
                            onChangeSelectedRows={setSelectedRows}
                            onUpdateRow={(newRow) => {
                                const updatedRows = rows.map((row) => (row.id === newRow.id ? newRow : row));
                                setRows(updatedRows);
                            }}
                            apiRef={previewGridApiRef}
                            isImportRecordAndObjectPanelOpened={sidePanelMode !== 'none'}
                        />

                        {showOptionsSidePanel && (
                            <ObjectPanel
                                selectedRow={rows.find((row) => +row?.id === +selectedOptionRowId)}
                                handleChangeOption={handleChangeOption}
                                handleDeleteOption={handleDeleteOption}
                                handleAddOption={handleAddOption}
                                properties={objectDefinition.objectProperties}
                            />
                        )}

                        {showSifImportResultCodes && (
                            <ImportResultCodes
                                sifRowsCodesStatus={sifRowsCodesStatus}
                                onClose={() => {
                                    setSidePanelMode('none');
                                }}
                            />
                        )}
                    </Grid>
                </DialogContent>

                <CustomDialogActions
                    onClose={onClose}
                    handleSubmit={handleSubmit}
                    selectedRows={selectedRows}
                    loadingSubmit={isSubmitting}
                />
            </Dialog>
            {/* <>
                {sifRowsCodesStatus && (
                    <AlertBox sifRowsCodesStatus={sifRowsCodesStatus} handleClose={() => setSifRowsCodesStatus(undefined)} />
                )}
            </> */}
        </>
    );
};
